import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { BackButton } from '../../components/back-button'
import {
  loadSavedAccounts,
  removeSavedAccount,
} from '@dogdisk/shared'
import type { SavedAccount } from '@dogdisk/shared'
import { MobileScreen } from '../../components/mobile-screen'
import { Download, X } from 'lucide-react'
import { ExportAccountsModal } from '../../components/modals/export-accounts-modal'
import { resourceUrl } from '../../_lib/resource-node-pref'

export default function LoginSavedPage() {
  const navigate = useNavigate()
  const [saved, setSaved] = useState<SavedAccount[]>([])
  const [hydrated, setHydrated] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)

  useEffect(() => {
    setSaved(loadSavedAccounts())
    setHydrated(true)
  }, [])

  if (hydrated && saved.length === 0) {
    return <Navigate to="/auth/login" replace />
  }

  const onRowClick = (a: SavedAccount): void => {
    navigate(`/auth/login?prefill_user_id=${encodeURIComponent(a.user_id)}`)
  }

  return (
    <MobileScreen>
      <main data-testid="fO6Sn" className="flex flex-1 flex-col">
        {/* Header: 返回 + 居中标题 + 44px 占位 */}
        <div className="flex items-center gap-3 px-6 pb-6 pt-[max(10px,env(safe-area-inset-top))]">
          <BackButton />
          <h1 className="flex-1 text-center text-[32px] font-bold text-[#1A1A1A]">已保存账号</h1>
          <div className="h-11 w-11" />
        </div>

        <div className="flex flex-col gap-6 px-6">
          <p className="text-[15px] text-[#9CA3AF]">选择一个账号快速登录</p>
          <ul className="flex flex-col gap-4">
            {saved.map((a) => (
              <li
                key={a.user_id}
                className="flex items-center gap-4 rounded-xl bg-[#F6F7F8] p-3"
              >
                {a.avatar_url ? (
                  <img
                    src={resourceUrl(a.avatar_url)}
                    alt={a.username}
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 shrink-0 rounded-full bg-[#19caad]" />
                )}
                <button
                  type="button"
                  data-testid="fO6Sn-account-item"
                  onClick={() => onRowClick(a)}
                  className="flex min-w-0 flex-1 flex-col text-left"
                >
                  <span className="truncate text-[15px] font-semibold text-[#1A1A1A]">
                    {a.username}
                  </span>
                  <span className="truncate text-[13px] text-[#9CA3AF]">
                    {maskIdentifier(a.username)}
                  </span>
                  <span className="text-xs text-[#9CA3AF]">
                    上次登录: {relTime(a.last_login_at)}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label="删除"
                  data-testid="fO6Sn-account-delete"
                  onClick={() => setSaved(removeSavedAccount(a.user_id))}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-[#9CA3AF] active:bg-[#EDEEF0]"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            data-testid="fO6Sn-add-account"
            onClick={() => navigate('/auth/login')}
            className="h-12 rounded-xl border-[1.5px] border-[#19caad] bg-white text-[16px] font-semibold text-[#19caad] active:bg-[#ebfaf6]"
          >
            + 添加新账号
          </button>
          <button
            type="button"
            data-testid="fO6Sn-export"
            onClick={() => setExportOpen(true)}
            className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#F6F7F8] text-[16px] font-semibold text-[#1A1A1A] active:bg-[#EDEEF0]"
          >
            <Download className="h-[18px] w-[18px]" aria-hidden="true" />
            导出账号密码
          </button>
        </div>
      </main>

      <ExportAccountsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        accounts={saved}
      />
    </MobileScreen>
  )
}

function maskIdentifier(username: string): string {
  if (!username) return ''
  const first = username.slice(0, 1).toLowerCase()
  const last = username.slice(-1).toLowerCase()
  if (username.includes('@')) {
    const atIdx = username.indexOf('@')
    const local = username.slice(0, atIdx)
    const domain = username.slice(atIdx)
    const localFirst = local.slice(0, 1).toLowerCase()
    const localLast = local.slice(-1).toLowerCase()
    return `${localFirst}***${localLast}${domain}`
  }
  return `${first}***${last}`
}

function relTime(iso: string): string {
  if (!iso) return '未知'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '未知'
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return '刚刚'
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} 天前`
  return `${Math.floor(diff / 86400 / 7)} 周前`
}
